import { PageSection } from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useState, useEffect } from 'react'

import { MedicationHistory } from '@app/components/patient/medication-history'
import { AddMedicationModal } from '@app/components/patient/medication-history/add-medication-modal'
import { EditRecordEntryModal } from '@app/components/patient/medication-history/edit-medication-record-entry-modal'
import type { IPatientId } from '@app/domain/patient/patient.interface'
import {
  usePatientMedicationHistory,
  usePatientMedicationRecordEntryDefaultValues,
  useMedicationRoutes,
  useMedicationDosageUnits,
  useMedicationFrequencyUnits,
} from '@app/domain/patient-medication-history/patient-medication-history.hooks'
import type {
  PatientMedicationId,
  PatientMedicationRecord,
  PatientMedicationRecordEntry,
  PatientMedication,
  PatientMedicationDosageUnitId,
  PatientMedicationRouteId,
} from '@app/domain/patient-medication-history/patient-medication-history.interface'
import { ValidationStatusId } from '@app/lib/validators'

export interface Props {
  patientId: IPatientId
}

export const PatientMedicationHistoryTabContent = ({ patientId }: Props) => {
  const [showErrorAlert, setShowErrorAlert] = useState(false)

  const [isAddMedicationModalOpen, setAddMedicationModalOpen] = useState(false)
  const [isEditRecordModalOpen, setEditRecordModalOpen] = useState(false)
  const [selectedMedication, setSelectedMedication] = useState<
    PatientMedication
  >(null)
  const [selectedRecord, setSelectedRecord] = useState<PatientMedicationRecord>(
    null,
  )

  const [isExistingRecordEntry, setIsExistingRecordEntry] = useState(false)
  const [currentRecordEntry, setCurrentRecordEntry] = useState<
    Partial<PatientMedicationRecordEntry>
  >(null)

  const { medicationRoutes } = useMedicationRoutes()
  const { medicationDosageUnits } = useMedicationDosageUnits()
  const { medicationFrequencyUnits } = useMedicationFrequencyUnits()

  const [conflictingError, setConflictingError] = useState<ValidationStatusId>(
    ValidationStatusId.Default,
  )

  const {
    recordEntryDefaults,
    getRecordEntryDefaults,
  } = usePatientMedicationRecordEntryDefaultValues()

  useEffect(() => {
    setCurrentRecordEntry(recordEntryDefaults)
  }, [recordEntryDefaults])

  const {
    addMedication,
    patientMedicationHistory,
    addMedicationRecord,
    updateMedication,
    updateMedicationRecord,
    isError,
    errorType,
    deleteMedicationRecord,
    deleteMedication,
    addOrUpdateMedicationRecordEntry,
  } = usePatientMedicationHistory(patientId)

  const openAddMedicationModal = useCallback(() => {
    setAddMedicationModalOpen(true)
  }, [])

  const onCloseAddMedicationModal = useCallback(() => {
    setSelectedMedication(null)
    setSelectedRecord(null)
    setAddMedicationModalOpen(false)
  }, [])

  const handleGetRecordEntryDefaultValues = useCallback(
    (
      selectedMedication: PatientMedication,
      selectedRecord: PatientMedicationRecord,
    ) => {
      setSelectedMedication(selectedMedication)
      setSelectedRecord(selectedRecord)
      getRecordEntryDefaults(
        patientId,
        selectedMedication.id,
        selectedRecord.id,
      )
      setEditRecordModalOpen(true)
    },
    [getRecordEntryDefaults, patientId],
  )

  const openEditRecordEntryModal = useCallback(
    (
      patientMedication: PatientMedication,
      medicationRecord: PatientMedicationRecord,
    ) => {
      setSelectedMedication(patientMedication)
      setSelectedRecord(medicationRecord)

      const entries: PatientMedicationRecordEntry[] = patientMedicationHistory.medicationRecordEntries.filter(
        (e) =>
          e.patientMedicationId === patientMedication.id &&
          e.patientMedicationRecordId === medicationRecord.id,
      )

      if (entries.length === 1) {
        setCurrentRecordEntry(entries[0])
        setIsExistingRecordEntry(true)
      } else if (entries.length > 1) {
        setCurrentRecordEntry(entries[0])
        setConflictingError(ValidationStatusId.Warning)
        setIsExistingRecordEntry(true)
      }
      setEditRecordModalOpen(true)
    },
    [patientMedicationHistory],
  )

  const onCloseEditRecordEntryModal = useCallback(() => {
    setIsExistingRecordEntry(false)
    setConflictingError(ValidationStatusId.Default)
    setEditRecordModalOpen(false)
    setCurrentRecordEntry(null)
  }, [])

  const handleAddMedication = useCallback(
    (
      medicationDosageUnitId: PatientMedicationDosageUnitId,
      medicationRouteId: PatientMedicationRouteId,
      name: string,
    ) => {
      setShowErrorAlert(false)
      addMedication(medicationDosageUnitId, medicationRouteId, name)
      setAddMedicationModalOpen(false)
    },
    [addMedication],
  )

  const handleAddMedicationRecord = useCallback(
    (activeDate: string) => {
      setShowErrorAlert(false)
      addMedicationRecord(activeDate)
    },
    [addMedicationRecord],
  )

  useEffect(() => {
    if (isError) {
      setShowErrorAlert(true)
    }
  }, [isError])

  const handleEditMedicationRecordEntry = useCallback(
    (recordEntryValues: PatientMedicationRecordEntry) => {
      addOrUpdateMedicationRecordEntry(
        recordEntryValues.patientMedicationId,
        recordEntryValues.patientMedicationRecordId,
        recordEntryValues.status,
        recordEntryValues.dosage,
        recordEntryValues.frequency,
        recordEntryValues.frequencyUnitId,
        recordEntryValues.indications,
        recordEntryValues.prescriber,
        recordEntryValues.comments,
        recordEntryValues.recommendations,
      )
    },
    [addOrUpdateMedicationRecordEntry],
  )

  const handleEditMedication = useCallback(
    (
      patientMedicationId: PatientMedicationId,
      medicationDosageUnitId: PatientMedicationDosageUnitId,
      medicationRouteId: PatientMedicationRouteId,
      name: string,
    ) => {
      setShowErrorAlert(false)
      updateMedication(
        patientMedicationId,
        medicationDosageUnitId,
        medicationRouteId,
        name,
      )
    },
    [updateMedication],
  )

  const handleUpdateMedicationRecord = useCallback(
    (activeDate: string, medicationRecord: PatientMedicationRecord) => {
      setShowErrorAlert(false)
      updateMedicationRecord(medicationRecord.id, activeDate)
    },
    [updateMedicationRecord],
  )
  const handleCloseErrorAlert = useCallback(() => {
    setShowErrorAlert(false)
  }, [])

  const handleDeleteRecord = useCallback(
    (medicationRecordId: string) => {
      setShowErrorAlert(false)
      deleteMedicationRecord(medicationRecordId)
    },
    [deleteMedicationRecord],
  )

  const handleDeleteMedication = useCallback(
    (patientMedicationId: string) => {
      setShowErrorAlert(false)
      deleteMedication(patientMedicationId)
    },
    [deleteMedication],
  )

  if (patientMedicationHistory && medicationRoutes && medicationDosageUnits) {
    return (
      <>
        <AddMedicationModal
          addMedication={handleAddMedication}
          isModalOpen={isAddMedicationModalOpen}
          medicationRoutes={medicationRoutes}
          medicationDosageUnits={medicationDosageUnits}
          onCancel={onCloseAddMedicationModal}
        />
        {selectedMedication && selectedRecord ? (
          <EditRecordEntryModal
            conflictingError={conflictingError}
            currentRecordEntry={currentRecordEntry}
            editRecordEntry={handleEditMedicationRecordEntry}
            isModalOpen={isEditRecordModalOpen}
            isExistingRecordEntry={isExistingRecordEntry}
            medicationFrequencyUnits={medicationFrequencyUnits}
            medicationRoutes={medicationRoutes}
            medicationRecordEntries={
              patientMedicationHistory.medicationRecordEntries
            }
            medicationDosageUnits={medicationDosageUnits}
            onCancel={onCloseEditRecordEntryModal}
            selectedMedication={selectedMedication}
            selectedRecord={selectedRecord}
          />
        ) : null}

        <PageSection>
          <MedicationHistory
            addMedicationRecord={handleAddMedicationRecord}
            addMedicationRecordEntry={handleGetRecordEntryDefaultValues}
            closeErrorAlert={handleCloseErrorAlert}
            editMedicationRecordEntry={openEditRecordEntryModal}
            editMedication={handleEditMedication}
            deleteRecord={handleDeleteRecord}
            deleteMedication={handleDeleteMedication}
            medicationHistory={patientMedicationHistory}
            medicationRoutes={medicationRoutes}
            medicationDosageUnits={medicationDosageUnits}
            showError={showErrorAlert}
            errorType={errorType}
            updateMedicationRecord={handleUpdateMedicationRecord}
            medicationRecordEntries={
              patientMedicationHistory.medicationRecordEntries
            }
            medicationFrequencyUnits={medicationFrequencyUnits}
            openAddMedicationModal={openAddMedicationModal}
          />
        </PageSection>
      </>
    )
  } else {
    return <PageSection>No Medication History.</PageSection>
  }
}
